/**
 * NavigationHubRenderer — backend-driven hub cards.
 *
 * The backend owns persona access and destination availability. This renderer
 * presents every non-hidden card with a consistent state treatment and only
 * creates links for AVAILABLE and READ_ONLY destinations.
 */
import { Page, AppContentContainer } from '../layout';
import { PageEmptyState } from '../states';
import { HubGroup } from './hub/HubGroup';
import { isCardRenderable } from './hub/hub-utils';
import { useAppI18n } from '../../i18n/use-app-i18n';
import type { NavigationHubDescriptor } from '@finance/contracts';

export interface NavigationHubRendererProps {
  hub: NavigationHubDescriptor;
}

export function NavigationHubRenderer({ hub }: NavigationHubRendererProps) {
  const { text } = useAppI18n();
  const visibleGroups = (hub.groups ?? [])
    .map((group) => ({ group, visibleCards: (group.cards ?? []).filter(isCardRenderable) }))
    .filter(({ visibleCards }) => visibleCards.length > 0);

  return (
    <AppContentContainer width="wide">
      <Page
        title={text(hub.titleKey)}
        subtitle={hub.descriptionKey ? text(hub.descriptionKey) : undefined}
      >
        {visibleGroups.length === 0 ? (
          <PageEmptyState />
        ) : (
          visibleGroups.map(({ group, visibleCards }, index) => (
            <HubGroup
              key={group.id}
              id={group.id}
              titleKey={group.titleKey}
              cards={visibleCards}
              showDivider={index > 0}
              isLast={index === visibleGroups.length - 1}
            />
          ))
        )}
      </Page>
    </AppContentContainer>
  );
}
